import React, { useEffect, useState } from "react";
import axios from "axios";
import { BookOpen, ArrowRight, ArrowLeft, CheckCircle2, XCircle, Award, Lightbulb, RotateCcw, Brain, HandHeart, Sparkles, BookCheck, ExternalLink, Languages, GraduationCap, Stethoscope } from "lucide-react";
import { useT } from "../i18n";
import { PEDA } from "../constants/testIds";
import RotatingLogo from "./RotatingLogo";

const ICON_MAP = { BookOpen, Brain, HandHeart, Sparkles, BookCheck, Languages, GraduationCap };

export default function PedaHub({ lang, API, categories }) {
    const T = useT(lang);
    const isRtl = lang === "he" || lang === "ar";
    const ArrowFwd = isRtl ? ArrowLeft : ArrowRight;

    const [courseId, setCourseId] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [idx, setIdx] = useState(0);
    const [selected, setSelected] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [result, setResult] = useState(null);
    const [score, setScore] = useState(0);
    const [done, setDone] = useState(false);
    const [loading, setLoading] = useState(false);

    const startCourse = async (id) => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/quiz/questions`, { params: { courseId: id } });
            setQuestions(r.data || []);
            setCourseId(id);
            setIdx(0);
            setSelected(null);
            setSubmitted(false);
            setResult(null);
            setScore(0);
            setDone(false);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const submitAnswer = async () => {
        if (!selected) return;
        try {
            const r = await axios.post(`${API}/quiz/check`, {
                questionId: questions[idx].id,
                answer: selected,
            });
            setResult(r.data);
            setSubmitted(true);
            if (r.data.correct) setScore((s) => s + 1);
        } catch (e) { console.error(e); }
    };

    const next = () => {
        if (idx + 1 >= questions.length) {
            setDone(true);
            return;
        }
        setIdx((i) => i + 1);
        setSelected(null);
        setSubmitted(false);
        setResult(null);
    };

    const reset = () => {
        setCourseId(null);
        setQuestions([]);
        setIdx(0);
        setSelected(null);
        setSubmitted(false);
        setResult(null);
        setScore(0);
        setDone(false);
    };

    // Featured sample tests — internal simulations (Yael / Amiram)
    const externalTests = [
        {
            id: "yael-sample",
            icon: Languages,
            accent: "#1A365D",
            gradient: "linear-gradient(135deg,#0B1F3D 0%,#1A365D 70%, #D4761F 130%)",
            titleHe: "מבחני יע\"ל לדוגמה",
            titleAr: "نماذج اختبارات يعئيل (يَع\"ل)",
            descHe: "ידיעת השפה העברית — תרגול אקדמי לקראת הבחינה: הבנת הנקרא, אוצר מילים ודקדוק.",
            descAr: "اختبار اللغة العبرية لطلاب الجامعات — تدرّب على فهم النص، المفردات والقواعد.",
            ctaHe: "התחילו סימולציית יע\"ל",
            ctaAr: "ابدأ محاكاة يعئيل",
        },
        {
            id: "amiram-sample",
            icon: GraduationCap,
            accent: "#D4761F",
            gradient: "linear-gradient(135deg,#D4761F 0%,#B36318 70%,#1A365D 140%)",
            titleHe: "מבחני אמיר\"ם באנגלית",
            titleAr: "اختبارات أميرام بالإنجليزية",
            descHe: "סימולציה ממוחשבת באנגלית כשפה זרה — אוצר מילים, השלמת משפטים והבנת הנקרא.",
            descAr: "محاكاة محوسبة للغة الإنجليزية كلغة أجنبية — مفردات، إكمال جمل وفهم النص.",
            ctaHe: "התחילו סימולציית אמיר\"ם",
            ctaAr: "ابدأ محاكاة أميرام",
        },
    ];

    // Show category list
    if (!courseId) {
        return (
            <div className="fade-up">
                {/* Hero strip with rotating medallion */}
                <div className="relative overflow-hidden rounded-3xl mb-10 border border-[#1A365D]/30" style={{
                    background: "radial-gradient(ellipse at 85% 25%, rgba(212,118,31,0.45) 0%, transparent 55%), linear-gradient(135deg,#0B1F3D 0%,#1A365D 100%)"
                }}>
                    <div className="absolute inset-0 opacity-[0.14]" style={{
                        backgroundImage: "linear-gradient(rgba(212,175,55,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.5) 1px, transparent 1px)",
                        backgroundSize: "44px 44px"
                    }} />
                    <div className="relative z-10 grid md:grid-cols-[auto_1fr] items-center gap-6 px-6 sm:px-10 py-8">
                        <div className="flex justify-center md:justify-start">
                            <RotatingLogo size="md" lang={lang} withOrbit testId="peda-rotating-logo" />
                        </div>
                        <div className="text-center md:text-start">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/50 text-[#FFD66B] text-[10px] font-bold uppercase tracking-[0.18em] mb-3">
                                <Sparkles size={11} />
                                {lang === "ar" ? "مركز التعلم · مدعوم بالذكاء الاصطناعي" : "מרכז למידה · מופעל ע\"י AI"}
                            </div>
                            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white leading-tight">
                                {T.pedaTitle}
                            </h2>
                            <p className="text-white/85 mt-2 max-w-2xl text-sm md:text-base">{T.pedaSubtitle}</p>
                        </div>
                    </div>
                </div>

                {/* MyDoctor — rotating CTA medallion */}
                <a
                    href="https://mydoctor-prep-2.emergent.host"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative block overflow-hidden rounded-3xl mb-8 border border-[#1A365D]/30 mydoctor-cta group"
                    style={{
                        background: "radial-gradient(ellipse at 15% 50%, rgba(14,165,233,0.45) 0%, transparent 55%), radial-gradient(ellipse at 90% 90%, rgba(212,118,31,0.4) 0%, transparent 55%), linear-gradient(135deg,#0B1F3D 0%,#102B4F 60%,#1A365D 100%)"
                    }}
                    data-testid="peda-mydoctor-cta"
                >
                    <div className="absolute inset-0 opacity-[0.14]" style={{
                        backgroundImage: "linear-gradient(rgba(212,175,55,0.55) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.55) 1px, transparent 1px)",
                        backgroundSize: "40px 40px"
                    }} />
                    <div className="relative z-10 grid md:grid-cols-[auto_1fr_auto] items-center gap-6 px-6 sm:px-10 py-7">
                        {/* Rotating MyDoctor coin */}
                        <div className="flex justify-center md:justify-start">
                            <div className="medallion-scene medallion-md mydoctor-coin-wrap">
                                <span className="medallion-orbit" />
                                <div className="medallion-coin">
                                    {/* FRONT */}
                                    <div className="medallion-face medallion-front">
                                        <div className="medallion-rim mydoctor-rim">
                                            <div className="medallion-inner mydoctor-inner">
                                                <Stethoscope size={26} className="text-[#0EA5E9]" />
                                                <div className="mt-1 font-serif text-[15px] font-extrabold text-[#1A365D] leading-none">{lang === "ar" ? "تشيسر" : "הצ׳ייסר"}</div>
                                                <div className="text-[8px] font-bold tracking-[0.2em] text-[#D4761F] uppercase mt-0.5">CHASER 🦊</div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* BACK */}
                                    <div className="medallion-face medallion-back">
                                        <div className="medallion-rim mydoctor-rim">
                                            <div className="medallion-inner mydoctor-inner">
                                                <div className="font-serif text-[13px] font-extrabold text-[#1A365D] leading-tight text-center">
                                                    {lang === "ar" ? "مركز" : "מרכז"}
                                                </div>
                                                <div className="font-serif text-[13px] font-extrabold text-[#D4761F] leading-tight text-center">
                                                    {lang === "ar" ? "التحضير" : "הכנה"}
                                                </div>
                                                <ArrowFwd size={14} className="text-[#0EA5E9] mt-1" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Copy */}
                        <div className="text-center md:text-start">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0EA5E9]/15 border border-[#0EA5E9]/40 text-[#7DD3FC] text-[10px] font-bold uppercase tracking-[0.18em] mb-2">
                                <Sparkles size={11} />
                                {lang === "ar" ? "تشيسر 🦊 — منصة التعلّم الذكية" : "הצ׳ייסר 🦊 — פלטפורמת הלמידה החכמה"}
                            </div>
                            <h3 className="font-serif text-xl md:text-2xl font-bold text-white leading-tight">
                                {lang === "ar"
                                    ? "تحضير لامتحانات الرخصة والأكاديميا مع تشيسر"
                                    : "הכנה למבחני רישוי ואקדמיה עם הצ׳ייסר"}
                            </h3>
                            <p className="text-white/85 text-sm mt-1.5">
                                {lang === "ar"
                                    ? "🩺 طب · 🏥 تمريض · ⚖️ حقوق · 📚 تربية · 🦷 طب أسنان · 💊 صيدلة"
                                    : "🩺 רפואה · 🏥 סיעוד · ⚖️ משפטים · 📚 חינוך · 🦷 רפואת שיניים · 💊 רוקחות"}
                            </p>
                            <p className="text-white/70 text-xs mt-1.5">
                                {lang === "ar"
                                    ? "✨ 248 كورسًا · 66,000+ سؤالًا · 721+ فيديو · تعلّم تكيّفي بالذكاء الاصطناعي"
                                    : "✨ 248 קורסים · 66,000+ שאלות · 721+ סרטונים · למידה אדפטיבית עם AI"}
                            </p>
                        </div>

                        {/* CTA */}
                        <div className="flex justify-center md:justify-end">
                            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0EA5E9] to-[#0284C7] hover:from-[#0284C7] hover:to-[#0EA5E9] transition-all text-white px-5 py-3 rounded-xl font-bold shadow-[0_10px_30px_rgba(14,165,233,0.45)] group-hover:scale-105 transform">
                                {lang === "ar" ? "افتح تشيسر" : "פתח את הצ׳ייסר"}
                                <ExternalLink size={16} />
                            </div>
                        </div>
                    </div>
                </a>

                {/* Featured external exam prep — Yael & Amiram */}
                <div className="mb-10">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <h3 className="font-bold text-[#1A365D] text-lg">
                            {lang === "ar" ? "اختبارات قبول جامعية — تدريب خارجي" : "מבחני סף אוניברסיטאיים — תרגול חיצוני"}
                        </h3>
                        <span className="text-xs text-[#D4761F] font-bold uppercase tracking-wider inline-flex items-center gap-1">
                            <Sparkles size={12} />
                            {lang === "ar" ? "موصى به" : "מומלץ"}
                        </span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        {externalTests.map((t) => {
                            const Icon = t.icon;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => startCourse(t.id)}
                                    className="relative overflow-hidden rounded-2xl p-6 text-white card-elevate group block text-start w-full"
                                    style={{ background: t.gradient }}
                                    data-testid={`peda-sample-${t.id}`}
                                >
                                    <div className="absolute inset-0 opacity-[0.18]" style={{
                                        backgroundImage: "linear-gradient(rgba(255,255,255,0.55) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.55) 1px, transparent 1px)",
                                        backgroundSize: "36px 36px",
                                        maskImage: "radial-gradient(ellipse at center, black 30%, transparent 80%)",
                                        WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 80%)"
                                    }} />
                                    <div className="relative z-10">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur border border-white/30 flex items-center justify-center">
                                                <Icon size={24} className="text-white" />
                                            </div>
                                            <Sparkles size={18} className="text-white/70 group-hover:text-white transition" />
                                        </div>
                                        <h4 className="font-serif text-xl md:text-2xl font-bold leading-tight">
                                            {lang === "ar" ? t.titleAr : t.titleHe}
                                        </h4>
                                        <p className="text-white/85 text-sm mt-2 leading-relaxed">
                                            {lang === "ar" ? t.descAr : t.descHe}
                                        </p>
                                        <div className="mt-5 inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/30 px-4 py-2 rounded-lg font-bold text-sm group-hover:bg-white/25 transition">
                                            {lang === "ar" ? t.ctaAr : t.ctaHe}
                                            <ArrowFwd size={16} />
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <h3 className="font-bold text-[#1A365D] mb-4 text-lg">{T.chooseCourse}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categories.filter((c) => c.id !== "yael-sample" && c.id !== "amiram-sample").map((c) => {
                        const Icon = ICON_MAP[c.icon] || BookOpen;
                        const accent = c.accent || "#1A365D";
                        return (
                            <button
                                key={c.id}
                                onClick={() => startCourse(c.id)}
                                className="bg-white border border-[#E2E8F0] rounded-2xl p-6 card-elevate text-right block w-full"
                                data-testid={PEDA.courseCard(c.id)}
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div className="px-2.5 py-1 rounded-md text-white text-xs font-bold tracking-wide" style={{ background: accent }}>
                                        {c.questionCount} {lang === "ar" ? "أسئلة" : "שאלות"}
                                    </div>
                                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${accent}15` }}>
                                        <Icon size={22} color={accent} />
                                    </div>
                                </div>
                                <h3 className="font-serif text-lg font-bold text-[#0F172A] mb-2 leading-snug">
                                    {lang === "ar" ? c.nameAr : c.nameHe}
                                </h3>
                                <p className="text-sm text-[#475569] leading-relaxed line-clamp-3">
                                    {lang === "ar" ? c.descriptionAr : c.descriptionHe}
                                </p>
                                <div className="mt-4 inline-flex items-center gap-2 font-semibold text-sm" style={{ color: accent }}>
                                    {T.startQuiz}
                                    <ArrowFwd size={16} />
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (loading || questions.length === 0) {
        return (
            <div className="text-center py-20">
                <button onClick={reset} className="btn-outline" data-testid={PEDA.backToCoursesBtn}>
                    {T.backToCourses}
                </button>
                <p className="text-[#475569] mt-6">{lang === "ar" ? "لا توجد أسئلة لهذا المساق بعد." : "אין שאלות בתחום זה כרגע."}</p>
            </div>
        );
    }

    // Done view
    if (done) {
        const pct = Math.round((score / questions.length) * 100);
        return (
            <div className="fade-up max-w-xl mx-auto text-center py-12">
                <div className="w-20 h-20 rounded-full bg-[#D4AF37] mx-auto flex items-center justify-center mb-6">
                    <Award size={40} color="#fff" />
                </div>
                <h2 className="font-serif text-3xl font-bold text-[#1A365D]">{T.quizComplete}</h2>
                <p className="text-[#475569] mt-2">{T.yourScore}</p>
                <div className="font-serif text-6xl font-bold text-[#D4AF37] my-6">{score} / {questions.length}</div>
                <div className="text-2xl text-[#1A365D] font-bold">{pct}%</div>
                <div className="mt-8 flex gap-3 justify-center">
                    <button onClick={() => startCourse(courseId)} className="btn-royal">
                        <RotateCcw size={16} />
                        {lang === "ar" ? "أعد المحاولة" : "נסה שוב"}
                    </button>
                    <button onClick={reset} className="btn-outline" data-testid={PEDA.backToCoursesBtn}>
                        {T.backToCourses}
                    </button>
                </div>
            </div>
        );
    }

    const q = questions[idx];
    return (
        <div className="fade-up max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-5">
                <button onClick={reset} className="btn-outline !py-1.5 !px-3 !text-sm" data-testid={PEDA.backToCoursesBtn}>
                    {T.backToCourses}
                </button>
                <div className="text-sm text-[#475569]">
                    {T.questionOf.replace("{curr}", idx + 1).replace("{total}", questions.length)}
                </div>
            </div>

            {/* Progress bar */}
            <div className="h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden mb-6">
                <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${((idx + 1) / questions.length) * 100}%` }} />
            </div>

            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 md:p-8 shadow-[0_14px_40px_rgba(15,23,42,0.05)]">
                <h3 className="font-serif text-xl md:text-2xl font-semibold text-[#0F172A] leading-relaxed mb-6">
                    {q.question}
                </h3>

                <div className="space-y-3">
                    {["A", "B", "C", "D"].map((key) => {
                        let cls = "quiz-option";
                        if (submitted) {
                            if (key === result?.correctAnswer) cls += " correct";
                            else if (key === selected && !result?.correct) cls += " wrong";
                        } else if (selected === key) cls += " selected";
                        return (
                            <button
                                key={key}
                                disabled={submitted}
                                onClick={() => setSelected(key)}
                                className={`${cls} w-full text-right`}
                                data-testid={PEDA.optionBtn(key)}
                            >
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 font-bold text-sm ${submitted && key === result?.correctAnswer ? "bg-green-500 text-white" : (selected === key ? "bg-[#1A365D] text-white" : "bg-[#F8F9FA] text-[#1A365D] border border-[#E2E8F0]")}`}>
                                    {key}
                                </div>
                                <span className="text-[#0F172A] text-sm leading-relaxed flex-1">{q.options[key]}</span>
                                {submitted && key === result?.correctAnswer && <CheckCircle2 size={20} color="#22C55E" />}
                                {submitted && key === selected && !result?.correct && <XCircle size={20} color="#EF4444" />}
                            </button>
                        );
                    })}
                </div>

                {submitted && result && (
                    <div className={`mt-6 rounded-xl p-4 border-r-4 ${result.correct ? "bg-green-50 border-green-500" : "bg-red-50 border-red-500"}`} data-testid={PEDA.rationaleBox}>
                        <div className="flex items-center gap-2 font-bold mb-2">
                            {result.correct ? (
                                <><CheckCircle2 size={20} color="#22C55E" /><span className="text-green-700">{T.correct}</span></>
                            ) : (
                                <><XCircle size={20} color="#EF4444" /><span className="text-red-700">{T.wrong}</span></>
                            )}
                        </div>
                        <div className="flex items-start gap-2">
                            <Lightbulb size={16} color="#D4AF37" className="mt-1 shrink-0" />
                            <div>
                                <div className="text-xs uppercase tracking-wider font-bold text-[#1A365D] mb-1">{T.explanation}</div>
                                <p className="text-sm text-[#0F172A] leading-relaxed">{result.rationale}</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-6 flex gap-3 justify-end">
                    {!submitted ? (
                        <button onClick={submitAnswer} disabled={!selected} className="btn-royal" data-testid={PEDA.submitAnswerBtn}>
                            {T.submitAnswer}
                            <ArrowFwd size={16} />
                        </button>
                    ) : (
                        <button onClick={next} className="btn-gold" data-testid={PEDA.nextQuestionBtn}>
                            {idx + 1 >= questions.length ? T.quizComplete : T.nextQuestion}
                            <ArrowFwd size={16} />
                        </button>
                    )}
                </div>
            </div>

            <div className="text-center mt-4 text-sm text-[#475569]">
                {T.score}: <span className="font-bold text-[#1A365D]">{score} / {idx + (submitted ? 1 : 0)}</span>
            </div>
        </div>
    );
}
